import { Calendar, FileText, History, X } from 'lucide-react';
import { formatDateFR } from '../utils';

export type HistoryEntry = {
  id: string;
  date: string;
  note?: string;
  auteur?: string;
  source?: string;
};

interface Props {
  title?: string;
  subtitle?: string;
  entries: HistoryEntry[];
  onClose: () => void;
}

/**
 * Modal partagé d'historique de relances.
 * Utilisé par Relances Client ET Relances Factures pour garantir
 * une expérience cohérente — même format, même tri, mêmes infos.
 */
export default function HistoryRelancesModal({ title = 'Historique des relances', subtitle, entries, onClose }: Props) {
  const sorted = entries.slice().sort((a, b) => b.date.localeCompare(a.date));
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-xl p-5 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-100 flex items-center gap-2">
            <History size={18} strokeWidth={1.5} className="text-slate-300" />
            {title}
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-200">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        {subtitle && <div className="text-xs text-slate-400 mb-3">{subtitle}</div>}
        <div className="overflow-y-auto space-y-2">
          {sorted.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-6">Pas d’historique pour l’instant.</div>
          )}
          {sorted.map((e) => (
            <div key={e.id} className="p-3 rounded-md border border-slate-800 bg-slate-950/60">
              <div className="flex items-center gap-2 text-xs text-slate-300 flex-wrap">
                <Calendar size={11} strokeWidth={1.5} className="text-emerald-400" />
                <span className="font-medium">
                  {formatDateFR(e.date, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </span>
                {e.auteur && <span className="text-slate-500">· {e.auteur}</span>}
                {e.source && e.source !== 'relance_client' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">
                    {e.source === 'relance_facture' ? 'Facture' : e.source === 'tache' ? 'Tâche' : e.source}
                  </span>
                )}
              </div>
              {e.note ? (
                <div className="text-xs text-slate-200 mt-1.5 flex gap-1.5 items-start">
                  <FileText size={11} strokeWidth={1.5} className="text-slate-500 mt-0.5 shrink-0" />
                  <span className="whitespace-pre-wrap">{e.note}</span>
                </div>
              ) : (
                <div className="text-xs text-slate-500 mt-1.5 italic">Aucune note</div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-xs border border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
